import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, Plus, FileText, ChevronLeft, ChevronRight, X, Brain } from 'lucide-react';
import { usePageTitle } from '../components/AppShell';
import { Toast, type ToastVariant } from '../components/Toast';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { hasRole, schoolIdFromUser } from '../lib/rbac';

const C = {
  yellow: '#F7D000', black: '#1A1A1A', white: '#FFFFFF',
  border: '#E5E7EB', gray50: '#F9FAFB', gray100: '#F3F4F6',
  gray200: '#E5E7EB', gray500: '#6B7280', gray700: '#374151', gray900: '#111827',
  success: '#166534', successBg: '#DCFCE7',
  error: '#991B1B', errorBg: '#FEE2E2',
  warning: '#92400E', warningBg: '#FEF3C7',
  info: '#1E40AF', infoBg: '#DBEAFE',
  purple: '#6D28D9', purpleBg: '#EDE9FE',
};

const BN = {
  title: 'Pelajar Keperluan Khas', subtitle: 'profil', back: '← Laman Utama',
  addProfile: '+ Tambah Profil SEN', tabs: ['Profil', 'Rancangan IEP', 'Statistik'] as const,
  search: 'Cari', searchPh: 'Nama, IC…', allCat: 'Semua Kategori',
  prev: 'Sebelum', next: 'Seterusnya', showing: 'Menunjukkan', to: 'hingga', of: 'daripada',
  emptyTitle: 'Tiada Profil SEN', emptyBody: 'Tiada rekod dijumpai.',
  addProfileTitle: 'Tambah Profil SEN', studentId: 'ID Pelajar *', category: 'Kategori *',
  diagnosis: 'Diagnosis *', diagnosedBy: 'Doktor/Pakar', diagnosisDate: 'Tarikh Diagnosis',
  supportLevel: 'Tahap Sokongan', notes: 'Nota', save: 'Simpan', saving: 'Menyimpan…', cancel: 'Batal',
  addIep: '+ Tambah IEP', iepTitle: 'Rancangan IEP Baharu', academicYear: 'Tahun Akademik *',
  startDate: 'Tarikh Mula *', reviewDate: 'Tarikh Semakan *', goals: 'Matlamat (satu per baris)',
  therapies: 'Terapi (satu per baris)', accommodations: 'Kemudahan',
  coordNotes: 'Nota Koordinator', iepStatus: 'Status IEP',
  totalSen: 'Jumlah Pelajar SEN', activeIeps: 'IEP Aktif', totalIeps: 'Jumlah IEP',
  retry: 'Cuba Lagi', errLoad: 'Gagal memuatkan.',
};

const EN = {
  title: 'Special Educational Needs', subtitle: 'profiles', back: '← Home',
  addProfile: '+ Add SEN Profile', tabs: ['Profiles', 'IEP Plans', 'Statistics'] as const,
  search: 'Search', searchPh: 'Name, IC…', allCat: 'All Categories',
  prev: 'Previous', next: 'Next', showing: 'Showing', to: 'to', of: 'of',
  emptyTitle: 'No SEN Profiles', emptyBody: 'No records found.',
  addProfileTitle: 'Add SEN Profile', studentId: 'Student ID *', category: 'Category *',
  diagnosis: 'Diagnosis *', diagnosedBy: 'Doctor/Specialist', diagnosisDate: 'Diagnosis Date',
  supportLevel: 'Support Level', notes: 'Notes', save: 'Save', saving: 'Saving…', cancel: 'Cancel',
  addIep: '+ Add IEP', iepTitle: 'New IEP Plan', academicYear: 'Academic Year *',
  startDate: 'Start Date *', reviewDate: 'Review Date *', goals: 'Goals (one per line)',
  therapies: 'Therapies (one per line)', accommodations: 'Accommodations',
  coordNotes: 'Coordinator Notes', iepStatus: 'IEP Status',
  totalSen: 'Total SEN Students', activeIeps: 'Active IEPs', totalIeps: 'Total IEPs',
  retry: 'Retry', errLoad: 'Failed to load.',
};

const SEN_CATEGORIES = [
  'LEARNING_DISABILITY','HEARING_IMPAIRMENT','VISUAL_IMPAIRMENT','PHYSICAL_DISABILITY',
  'AUTISM_SPECTRUM','SPEECH_LANGUAGE','INTELLECTUAL','GIFTED_TALENTED','MULTIPLE_DISABILITIES','OTHER',
];
const CAT_LABELS: Record<string, { ms: string; en: string; color: string }> = {
  LEARNING_DISABILITY:   { ms: 'Kesukaran Pembelajaran', en: 'Learning Disability',   color: '#60a5fa' },
  HEARING_IMPAIRMENT:    { ms: 'Masalah Pendengaran',    en: 'Hearing Impairment',    color: '#a78bfa' },
  VISUAL_IMPAIRMENT:     { ms: 'Masalah Penglihatan',    en: 'Visual Impairment',     color: '#34d399' },
  PHYSICAL_DISABILITY:   { ms: 'Kecacatan Fizikal',      en: 'Physical Disability',   color: '#fb923c' },
  AUTISM_SPECTRUM:       { ms: 'Spektrum Autisme',        en: 'Autism Spectrum',       color: '#f472b6' },
  SPEECH_LANGUAGE:       { ms: 'Pertuturan & Bahasa',    en: 'Speech & Language',     color: '#fbbf24' },
  INTELLECTUAL:          { ms: 'Intelektual',             en: 'Intellectual',          color: '#4ade80' },
  GIFTED_TALENTED:       { ms: 'Berbakat',                en: 'Gifted & Talented',     color: '#F7D000' },
  MULTIPLE_DISABILITIES: { ms: 'Pelbagai Keperluan',      en: 'Multiple Disabilities', color: '#f87171' },
  OTHER:                 { ms: 'Lain-lain',               en: 'Other',                 color: '#9ca3af' },
};
const IEP_STATUS_CFG: Record<string, { label: string; color: string; bg: string }> = {
  DRAFT:    { label: 'Draf',    color: '#92400E', bg: '#FEF3C7' },
  ACTIVE:   { label: 'Aktif',   color: '#166534', bg: '#DCFCE7' },
  REVIEWED: { label: 'Semakan', color: '#1E40AF', bg: '#DBEAFE' },
  CLOSED:   { label: 'Tutup',   color: '#4B5563', bg: '#F3F4F6' },
};

const cardShadow = '0 1px 3px rgba(0,0,0,0.08)';

interface Props { user: AuthUser }
interface SenProfile {
  id: string; student_id: string; category: string; diagnosis: string;
  diagnosed_by: string | null; diagnosis_date: string | null; support_level: string | null;
  notes: string | null; full_name_en: string | null; full_name_ms: string | null;
  ic_number: string; class_name: string | null; iep_count: string;
}
interface IepPlan {
  id: string; sen_profile_id: string; academic_year: string; start_date: string;
  review_date: string; goals: string; therapies: string; accommodations: string | null;
  coordinator_notes: string | null; status: string; coordinator_name: string | null;
}
interface Stats { byCategory: Array<{ category: string; cnt: string }>; iepStats: Array<{ status: string; cnt: string }> }
interface PageResult { data: SenProfile[]; total: number; page: number }

export function SENPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;
  usePageTitle(UI.title);

  const canEdit  = hasRole(user, 'super_admin', 'admin', 'principal', 'sen_coordinator');
  const schoolId = schoolIdFromUser(user) ?? '';

  const [tab, setTab]     = useState<'profiles' | 'ieps' | 'stats'>('profiles');
  const [profiles, setProfiles] = useState<SenProfile[]>([]);
  const [ieps,     setIeps]     = useState<IepPlan[]>([]);
  const [stats,    setStats]    = useState<Stats | null>(null);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const [search,   setSearch]   = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showAddIep,     setShowAddIep]     = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [profileForm, setProfileForm] = useState({
    studentId: '', category: 'LEARNING_DISABILITY', diagnosis: '',
    diagnosedBy: '', diagnosisDate: '', supportLevel: '', notes: '',
  });
  const [iepForm, setIepForm] = useState({
    senProfileId: '', academicYear: '', startDate: '', reviewDate: '',
    goals: '', therapies: '', accommodations: '', coordinatorNotes: '',
  });

  const LIMIT = 20;

  const loadProfiles = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = new URLSearchParams({ schoolId, page: String(page) });
      if (search.trim()) p.set('q', search.trim());
      if (catFilter) p.set('category', catFilter);
      const res = await apiGet<PageResult>(`/api/sen/profiles?${p}`);
      setProfiles(res.data); setTotal(res.total);
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, page, search, catFilter, UI.errLoad]);

  const loadIeps = useCallback(async () => {
    if (!selectedProfileId) return;
    setLoading(true); setError(null);
    try {
      setIeps(await apiGet<IepPlan[]>(`/api/sen/profiles/${selectedProfileId}/ieps`));
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [selectedProfileId, UI.errLoad]);

  const loadStats = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setStats(await apiGet<Stats>(`/api/sen/stats?schoolId=${schoolId}`));
    } catch (e) { setError(e instanceof Error ? e.message : UI.errLoad); }
    finally { setLoading(false); }
  }, [schoolId, UI.errLoad]);

  useEffect(() => { if (tab === 'profiles') loadProfiles(); }, [tab, loadProfiles]);
  useEffect(() => { if (tab === 'ieps') loadIeps(); }, [tab, loadIeps]);
  useEffect(() => { if (tab === 'stats') loadStats(); }, [tab, loadStats]);

  const submitProfile = async () => {
    if (!profileForm.studentId.trim() || !profileForm.diagnosis.trim()) return;
    setSaving(true);
    try {
      await apiPost('/api/sen/profiles', {
        studentId: profileForm.studentId, category: profileForm.category,
        diagnosis: profileForm.diagnosis,
        diagnosedBy: profileForm.diagnosedBy || undefined,
        diagnosisDate: profileForm.diagnosisDate || undefined,
        supportLevel: profileForm.supportLevel || undefined,
        notes: profileForm.notes || undefined,
      });
      setShowAddProfile(false);
      setProfileForm({ studentId: '', category: 'LEARNING_DISABILITY', diagnosis: '', diagnosedBy: '', diagnosisDate: '', supportLevel: '', notes: '' });
      setToast({ message: lang === 'en' ? 'SEN profile created.' : 'Profil SEN dicipta.', variant: 'success' });
      loadProfiles();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const submitIep = async () => {
    if (!iepForm.senProfileId || !iepForm.academicYear.trim() || !iepForm.startDate || !iepForm.reviewDate) return;
    setSaving(true);
    try {
      await apiPost('/api/sen/ieps', {
        senProfileId: iepForm.senProfileId, academicYear: iepForm.academicYear,
        startDate: iepForm.startDate, reviewDate: iepForm.reviewDate,
        goals: iepForm.goals.split('\n').filter(Boolean),
        therapies: iepForm.therapies.split('\n').filter(Boolean),
        accommodations: iepForm.accommodations || undefined,
        coordinatorNotes: iepForm.coordinatorNotes || undefined,
      });
      setShowAddIep(false);
      setIepForm({ senProfileId: '', academicYear: '', startDate: '', reviewDate: '', goals: '', therapies: '', accommodations: '', coordinatorNotes: '' });
      setToast({ message: lang === 'en' ? 'IEP plan created.' : 'Rancangan IEP dicipta.', variant: 'success' });
      loadIeps();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
    finally { setSaving(false); }
  };

  const updateIepStatus = async (iepId: string, status: string) => {
    try {
      await apiPatch(`/api/sen/ieps/${iepId}/status`, { status });
      setToast({ message: lang === 'en' ? 'IEP status updated.' : 'Status IEP dikemas kini.', variant: 'success' });
      loadIeps();
    } catch (e) { setToast({ message: e instanceof Error ? e.message : 'Ralat.', variant: 'error' }); }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: '6px',
    border: `1px solid ${C.border}`, fontSize: '14px', color: C.gray900,
    backgroundColor: C.white, boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = { fontSize: '12px', fontWeight: 600, color: C.gray700, marginBottom: '4px', display: 'block' };

  return (
    <div style={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}

      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Link to="/" style={{ fontSize: '13px', color: C.gray500, textDecoration: 'none' }}>{UI.back}</Link>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: C.purpleBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Brain size={20} color={C.purple} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: C.gray900 }}>{UI.title}</h1>
              <p style={{ margin: 0, fontSize: '13px', color: C.gray500 }}>{total} {UI.subtitle}</p>
            </div>
          </div>
          {canEdit && tab === 'profiles' && (
            <button onClick={() => setShowAddProfile(true)} style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>
              {UI.addProfile}
            </button>
          )}
          {canEdit && tab === 'ieps' && selectedProfileId && (
            <button onClick={() => { setIepForm(f => ({ ...f, senProfileId: selectedProfileId })); setShowAddIep(true); }}
              style={{ padding: '9px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer', color: C.black }}>
              {UI.addIep}
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: `1px solid ${C.border}` }}>
        {(['profiles', 'ieps', 'stats'] as const).map((t, i) => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '10px 20px', border: 'none', background: 'transparent',
            fontSize: '14px', fontWeight: 600, cursor: 'pointer',
            color: tab === t ? C.black : C.gray500,
            borderBottom: tab === t ? `2px solid ${C.yellow}` : '2px solid transparent',
            marginBottom: '-1px',
          }}>{lang === 'en' ? EN.tabs[i] : BN.tabs[i]}</button>
        ))}
      </div>

      {/* Profiles tab */}
      {tab === 'profiles' && (
        <>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder={UI.searchPh}
              style={{ ...inputStyle, width: '220px' }}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(1); loadProfiles(); }}} />
            <select value={catFilter} onChange={e => { setCatFilter(e.target.value); setPage(1); }}
              style={{ ...inputStyle, width: '200px' }}>
              <option value="">{UI.allCat}</option>
              {SEN_CATEGORIES.map(c => <option key={c} value={c}>{lang === 'en' ? CAT_LABELS[c]?.en : CAT_LABELS[c]?.ms}</option>)}
            </select>
            <button onClick={() => { setPage(1); loadProfiles(); }}
              style={{ padding: '8px 16px', borderRadius: '6px', background: C.yellow, border: 'none', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}>
              {UI.search}
            </button>
          </div>

          {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
          {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px', marginBottom: '16px' }}>{error} <button onClick={loadProfiles} style={{ marginLeft: '8px', color: C.error, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>{UI.retry}</button></div>}

          {!loading && !error && profiles.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <UserCheck size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p style={{ fontWeight: 600, margin: 0 }}>{UI.emptyTitle}</p>
              <p style={{ margin: '4px 0 0', fontSize: '13px' }}>{UI.emptyBody}</p>
            </div>
          )}

          <div style={{ display: 'grid', gap: '10px' }}>
            {profiles.map(p => {
              const cat = CAT_LABELS[p.category];
              return (
                <div key={p.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{ fontWeight: 700, fontSize: '15px', color: C.gray900 }}>{lang === 'en' ? (p.full_name_en ?? p.ic_number) : (p.full_name_ms ?? p.full_name_en ?? p.ic_number)}</div>
                    <div style={{ fontSize: '12px', color: C.gray500 }}>IC: {p.ic_number} {p.class_name ? `· ${p.class_name}` : ''}</div>
                    <div style={{ fontSize: '12px', color: C.gray500, marginTop: '2px' }}>{p.diagnosis}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: cat?.color + '22', color: cat?.color }}>{lang === 'en' ? cat?.en : cat?.ms}</span>
                    <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: C.infoBg, color: C.info }}>{p.iep_count} IEP</span>
                    <button onClick={() => { setSelectedProfileId(p.id); setTab('ieps'); }}
                      style={{ padding: '6px 12px', borderRadius: '6px', background: C.gray100, border: `1px solid ${C.border}`, fontSize: '12px', cursor: 'pointer', fontWeight: 600, color: C.gray700 }}>
                      <FileText size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} />IEP
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {total > LIMIT && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', fontSize: '13px', color: C.gray500 }}>
              <span>{UI.showing} {(page - 1) * LIMIT + 1} {UI.to} {Math.min(page * LIMIT, total)} {UI.of} {total}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.5 : 1 }}><ChevronLeft size={14} /></button>
                <button disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)} style={{ padding: '6px 14px', borderRadius: '6px', border: `1px solid ${C.border}`, background: C.white, cursor: page * LIMIT >= total ? 'default' : 'pointer', opacity: page * LIMIT >= total ? 0.5 : 1 }}><ChevronRight size={14} /></button>
              </div>
            </div>
          )}
        </>
      )}

      {/* IEP tab */}
      {tab === 'ieps' && (
        <>
          {!selectedProfileId && (
            <div style={{ textAlign: 'center', padding: '60px', color: C.gray500 }}>
              <FileText size={40} style={{ opacity: 0.3, marginBottom: '12px' }} />
              <p>{lang === 'en' ? 'Select a student profile from the Profiles tab to view IEPs.' : 'Pilih profil pelajar dari tab Profil untuk melihat IEP.'}</p>
              <button onClick={() => setTab('profiles')} style={{ marginTop: '8px', padding: '8px 16px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 600, cursor: 'pointer' }}>
                {lang === 'en' ? 'Go to Profiles' : 'Ke Tab Profil'}
              </button>
            </div>
          )}
          {selectedProfileId && (
            <>
              {loading && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>…</div>}
              {error && <div style={{ padding: '16px', background: C.errorBg, color: C.error, borderRadius: '8px' }}>{error}</div>}
              <div style={{ display: 'grid', gap: '12px' }}>
                {ieps.map(iep => {
                  const sc = IEP_STATUS_CFG[iep.status] ?? IEP_STATUS_CFG['DRAFT']!;
                  const goals: string[] = (() => { try { return JSON.parse(iep.goals) as string[]; } catch { return []; }})();
                  const therapies: string[] = (() => { try { return JSON.parse(iep.therapies) as string[]; } catch { return []; }})();
                  return (
                    <div key={iep.id} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '16px', boxShadow: cardShadow }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                        <div>
                          <span style={{ fontWeight: 700, fontSize: '15px' }}>{iep.academic_year}</span>
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: C.gray500 }}>
                            {new Date(iep.start_date).toLocaleDateString('ms-BN')} – {new Date(iep.review_date).toLocaleDateString('ms-BN')}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: sc.bg, color: sc.color }}>{sc.label}</span>
                          {canEdit && (
                            <select value={iep.status} onChange={e => updateIepStatus(iep.id, e.target.value)}
                              style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '6px', border: `1px solid ${C.border}`, cursor: 'pointer' }}>
                              {Object.keys(IEP_STATUS_CFG).map(s => <option key={s} value={s}>{IEP_STATUS_CFG[s]?.label}</option>)}
                            </select>
                          )}
                        </div>
                      </div>
                      {goals.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: C.gray500, marginBottom: '4px' }}>{lang === 'en' ? 'GOALS' : 'MATLAMAT'}</div>
                          <ul style={{ margin: 0, paddingLeft: '16px' }}>
                            {goals.map((g, i) => <li key={i} style={{ fontSize: '13px', color: C.gray700, marginBottom: '2px' }}>{g}</li>)}
                          </ul>
                        </div>
                      )}
                      {therapies.length > 0 && (
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 600, color: C.gray500, marginBottom: '4px' }}>{lang === 'en' ? 'THERAPIES' : 'TERAPI'}</div>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {therapies.map((t, i) => <span key={i} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', background: C.purpleBg, color: C.purple }}>{t}</span>)}
                          </div>
                        </div>
                      )}
                      {iep.coordinator_notes && <p style={{ margin: '8px 0 0', fontSize: '12px', color: C.gray500, fontStyle: 'italic' }}>{iep.coordinator_notes}</p>}
                    </div>
                  );
                })}
                {!loading && ieps.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: C.gray500 }}>{UI.emptyTitle}</div>}
              </div>
            </>
          )}
        </>
      )}

      {/* Stats tab */}
      {tab === 'stats' && stats && (
        <div style={{ display: 'grid', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {[
              { label: UI.totalSen, value: stats.byCategory.reduce((s, c) => s + parseInt(c.cnt, 10), 0), color: C.purple, bg: C.purpleBg },
              { label: UI.activeIeps, value: stats.iepStats.find(s => s.status === 'ACTIVE') ? parseInt(stats.iepStats.find(s => s.status === 'ACTIVE')!.cnt, 10) : 0, color: C.success, bg: C.successBg },
              { label: UI.totalIeps, value: stats.iepStats.reduce((s, c) => s + parseInt(c.cnt, 10), 0), color: C.info, bg: C.infoBg },
            ].map(card => (
              <div key={card.label} style={{ background: card.bg, borderRadius: '10px', padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 800, color: card.color }}>{card.value}</div>
                <div style={{ fontSize: '13px', color: card.color, marginTop: '4px' }}>{card.label}</div>
              </div>
            ))}
          </div>
          <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: 700 }}>{lang === 'en' ? 'By Category' : 'Mengikut Kategori'}</h3>
            <div style={{ display: 'grid', gap: '8px' }}>
              {stats.byCategory.map(cat => {
                const cfg = CAT_LABELS[cat.category];
                const pct = Math.round((parseInt(cat.cnt, 10) / Math.max(1, stats.byCategory.reduce((s, c) => s + parseInt(c.cnt, 10), 0))) * 100);
                return (
                  <div key={cat.category}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                      <span style={{ color: C.gray700 }}>{lang === 'en' ? cfg?.en : cfg?.ms}</span>
                      <span style={{ fontWeight: 700, color: C.gray900 }}>{cat.cnt} ({pct}%)</span>
                    </div>
                    <div style={{ height: '6px', background: C.gray100, borderRadius: '3px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, background: cfg?.color ?? C.gray500, borderRadius: '3px', transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Add Profile Modal */}
      {showAddProfile && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.addProfileTitle}</h2>
              <button onClick={() => setShowAddProfile(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { key: 'studentId', label: UI.studentId, type: 'text' },
                { key: 'diagnosis', label: UI.diagnosis, type: 'text' },
                { key: 'diagnosedBy', label: UI.diagnosedBy, type: 'text' },
                { key: 'diagnosisDate', label: UI.diagnosisDate, type: 'date' },
                { key: 'supportLevel', label: UI.supportLevel, type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={profileForm[f.key as keyof typeof profileForm]}
                    onChange={e => setProfileForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={inputStyle} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.category}</label>
                <select value={profileForm.category} onChange={e => setProfileForm(p => ({ ...p, category: e.target.value }))} style={inputStyle}>
                  {SEN_CATEGORIES.map(c => <option key={c} value={c}>{lang === 'en' ? CAT_LABELS[c]?.en : CAT_LABELS[c]?.ms}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>{UI.notes}</label>
                <textarea value={profileForm.notes} onChange={e => setProfileForm(p => ({ ...p, notes: e.target.value }))}
                  style={{ ...inputStyle, height: '80px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submitProfile} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAddProfile(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add IEP Modal */}
      {showAddIep && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: C.white, borderRadius: '12px', padding: '24px', width: '540px', maxHeight: '80vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{UI.iepTitle}</h2>
              <button onClick={() => setShowAddIep(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'grid', gap: '12px' }}>
              {[
                { key: 'academicYear', label: UI.academicYear, type: 'text' },
                { key: 'startDate', label: UI.startDate, type: 'date' },
                { key: 'reviewDate', label: UI.reviewDate, type: 'date' },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <input type={f.type} value={iepForm[f.key as keyof typeof iepForm]}
                    onChange={e => setIepForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={inputStyle} />
                </div>
              ))}
              {[
                { key: 'goals', label: UI.goals },
                { key: 'therapies', label: UI.therapies },
              ].map(f => (
                <div key={f.key}>
                  <label style={labelStyle}>{f.label}</label>
                  <textarea value={iepForm[f.key as keyof typeof iepForm]}
                    onChange={e => setIepForm(p => ({ ...p, [f.key]: e.target.value }))}
                    style={{ ...inputStyle, height: '80px', resize: 'vertical' }} />
                </div>
              ))}
              <div>
                <label style={labelStyle}>{UI.accommodations}</label>
                <input type="text" value={iepForm.accommodations} onChange={e => setIepForm(p => ({ ...p, accommodations: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>{UI.coordNotes}</label>
                <textarea value={iepForm.coordinatorNotes} onChange={e => setIepForm(p => ({ ...p, coordinatorNotes: e.target.value }))}
                  style={{ ...inputStyle, height: '60px', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={submitIep} disabled={saving} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.yellow, border: 'none', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? UI.saving : UI.save}
              </button>
              <button onClick={() => setShowAddIep(false)} style={{ flex: 1, padding: '10px', borderRadius: '8px', background: C.gray100, border: `1px solid ${C.border}`, fontWeight: 600, cursor: 'pointer' }}>{UI.cancel}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
